import React, { useEffect, useState, useRef } from 'react';
import RecordRTC, { RecordRTCPromisesHandler } from 'recordrtc';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Picker from '@emoji-mart/react';
import { TimezoneSelect } from '../components/TimezoneSelect';
import {
  createMessageScheduling,
  deleteMessageScheduling,
  getMessageSchedulings,
  getMessageSchedulingsPaged,
  updateMessageScheduling,
  getMessageScheduling,
  uploadMessageSchedulingAttachment,
  CreateMessageSchedulingDTO,
  RecipientDTO
} from '../services/MessageSchedulingsService';
import { getTags, Tag } from '../services/LabelService';
import { getInternalGroups, InternalGroup } from '../services/InternalGroupService';
import { getWhatsAppGroups, WhatsAppGroupDto } from '../services/SectorService';
import { getContacts, getContactsWithFilters, Contact } from '../services/ContactService';
import SessionService from '../services/SessionService';
import { getDisplayPhone } from '../utils/formatters';
import { config } from '../config';
import './MessageSchedule.css';
import { LoadingSpinner } from '../components/LoadingSpinner';
import Toast, { ToastContainer } from '../components/Toast';
import { useNavigate } from 'react-router-dom';
import { ConfigureTimezoneModal } from '../components/ConfigureTimezoneModal';
import { ConfirmModal } from '../components/SharedModal';
import { dateParts, recipientsLabel, relativeWhen, scheduleStatus, utcInstantMs } from '../utils/scheduleDisplay';

dayjs.extend(customParseFormat);
dayjs.extend(timezone);
dayjs.extend(utc);

// Função para obter o fuso horário local
const getLocalTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Função para criar uma data no fuso horário local
const createLocalDate = (date?: string | Date | dayjs.Dayjs) => {
  const localTz = getLocalTimezone();
  if (date) {
    return dayjs(date).tz(localTz);
  }
  return dayjs().tz(localTz);
};



declare global {
  interface Window {
    google: any;
    gapi: any;
  }
}

const google = window.google;
const gapi = window.gapi;

const Icons = {
  Plus: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  ),
  Calendar: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="16" y1="2" x2="16" y2="6"></line>
      <line x1="8" y1="2" x2="8" y2="6"></line>
      <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
  ),
  Clock: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12 6 12 12 16 14"></polyline>
    </svg>
  ),
  Edit: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
    </svg>
  ),
  Delete: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 6h18"></path>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    </svg>
  ),
  Clone: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
    </svg>
  ),
  Paperclip: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
    </svg>
  ),
  Picture: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <circle cx="8.5" cy="8.5" r="1.5"></circle>
      <polyline points="21 15 16 10 5 21"></polyline>
    </svg>
  ),
  Video: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="23 7 16 12 23 17 23 7"></polygon>
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
    </svg>
  ),
  Mic: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
      <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
      <line x1="12" y1="19" x2="12" y2="23"></line>
      <line x1="8" y1="23" x2="16" y2="23"></line>
    </svg>
  ),
  ChevronLeft: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
  ),
  ChevronRight: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="9 18 15 12 9 6"></polyline>
    </svg>
  ),
};

interface Attachment {
  fileType: 'image' | 'audio' | 'video' | 'file';
  data: string;
  name: string;
  preview?: string;
  duration?: number;
  mimeType: string;
  fileUrl?: string;
  /** Raw file kept for multipart upload before create (avoids huge JSON base64). */
  file?: File | Blob;
}

interface Message {
  id?: number;
  title: string;
  date: number | null;
  description: string;
  labels: string[];
  contactId: number | null;
  contact: Contact;
  attachments?: Attachment[];
  location?: string;
  timezone?: string;
  /** Pending | Processing | Completed | Failed (SchedulerWorker). */
  status?: string;
}

const SCOPES = 'https://www.googleapis.com/auth/calendar';

interface MessageScheduleProps {
  visible: boolean;
  onClose: () => void;
  onSave: () => void;
  contactId: number;
  addToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

interface BackendAttachment {
  type: string;
  data: string;
  name: string;
  preview?: string;
  duration?: number;
}

// Componente de calendário customizado - movido para fora do escopo principal
const CustomCalendar: React.FC<{
  selectedDate: dayjs.Dayjs | null;
  onChange: (date: dayjs.Dayjs) => void;
  onClose?: () => void;
  viewMode?: 'day' | 'month';
  timezone?: string;
}> = ({ selectedDate, onChange, viewMode = 'day', timezone }) => {
  const localNow = createLocalDate();
  const [currentMonth, setCurrentMonth] = useState(
    selectedDate ? selectedDate.month() : localNow.month()
  );
  const [currentYear, setCurrentYear] = useState(
    selectedDate ? selectedDate.year() : localNow.year()
  );
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  // Reset visual state when viewMode changes just in case, though usually controlled by parent
  useEffect(() => {
    // If switching to month view, maybe center on selected year? Already done by state init.
  }, [viewMode]);

  const daysInMonth = createLocalDate().year(currentYear).month(currentMonth).daysInMonth();
  const firstDayOfMonth = createLocalDate().year(currentYear).month(currentMonth).startOf('month').day();

  const daysGrid = Array.from({ length: 42 }, (_, i) => {
    const adjustedIndex = i - firstDayOfMonth + 1;
    return adjustedIndex > 0 && adjustedIndex <= daysInMonth ? adjustedIndex : null;
  });

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const prevYear = () => {
    setCurrentYear(currentYear - 1);
  };

  const nextYear = () => {
    setCurrentYear(currentYear + 1);
  };

  const handleDateClick = (day: number) => {
    if (day !== null) {
      const baseDate = selectedDate ? selectedDate.clone() : (timezone ? dayjs().tz(timezone) : createLocalDate());
      const newDate = baseDate.year(currentYear).month(currentMonth).date(day);
      onChange(newDate);
    }
  };

  const handleMonthClick = (monthIndex: number) => {
    // Defaults to end of month for "End Date" logic or start of month? 
    // User said "Se for mes, janeiro fevereiro, etc... na data final".
    // Usually end date means "until the end of...". 
    // Let's set it to the last day of the selected month to be safe for an "End Date".
    // Or just the 1st day and let backend handle? 
    // Let's stick to the clicked month, day 1, or preserve day if valid?
    // Standard for "Select Month" is usually 1st of month.
    // But for recurrence end date, "End of January" implies Jan 31st.
    const newDate = createLocalDate().year(currentYear).month(monthIndex).endOf('month');
    onChange(newDate);
  };

  const isToday = (day: number): boolean => {
    const today = createLocalDate();
    return (
      day === today.date() &&
      currentMonth === today.month() &&
      currentYear === today.year()
    );
  };

  const isSelected = (day: number): boolean => {
    if (!selectedDate) return false;
    return (
      day === selectedDate.date() &&
      currentMonth === selectedDate.month() &&
      currentYear === selectedDate.year()
    );
  };

  const monthNames = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
  ];

  if (viewMode === 'month') {
    return (
      <div className="custom-calendar month-view">
        <div className="calendar-header">
          <button className="calendar-nav-button" onClick={prevYear}>
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="none" stroke="currentColor" strokeWidth="2" d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div className="calendar-month-year">
            <span>{currentYear}</span>
          </div>
          <button className="calendar-nav-button" onClick={nextYear}>
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="none" stroke="currentColor" strokeWidth="2" d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
        <div className="calendar-months-grid">
          {monthNames.map((mName, idx) => (
            <div
              key={idx}
              className={`calendar-month-item ${selectedDate && selectedDate.month() === idx && selectedDate.year() === currentYear ? 'selected' : ''}`}
              onClick={() => handleMonthClick(idx)}
            >
              <span>{mName.substring(0, 3)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="custom-calendar day-view">
      <div className="calendar-header">
        <button className="calendar-nav-button" onClick={prevMonth}>
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="none" stroke="currentColor" strokeWidth="2" d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <div className="calendar-month-year">
          <span>{monthNames[currentMonth]} {currentYear}</span>
        </div>
        <button className="calendar-nav-button" onClick={nextMonth}>
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="none" stroke="currentColor" strokeWidth="2" d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      <div className="calendar-weekdays">
        {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((day, index) => (
          <div key={index} className="calendar-weekday"><span>{day}</span></div>
        ))}
      </div>

      <div className="calendar-days">
        {daysGrid.map((day, index) => (
          <div
            key={index}
            className={`calendar-day ${day === null ? 'empty' : ''} ${day !== null && isToday(day) ? 'today' : ''
              } ${day !== null && isSelected(day) ? 'selected' : ''} ${day !== null && hoveredDay === day && !isSelected(day) ? 'hovered' : ''
              }`}
            onClick={() => day !== null && handleDateClick(day)}
            onMouseEnter={() => day !== null && setHoveredDay(day)}
            onMouseLeave={() => setHoveredDay(null)}
          >
            {day !== null ? <span>{day}</span> : null}
          </div>
        ))}
      </div>
    </div>
  );
};

// Componente de seleção de hora - movido para fora do escopo principal
const TimeSelector: React.FC<{
  selectedTime: dayjs.Dayjs | null;
  onChange: (time: dayjs.Dayjs) => void;
  onClose?: () => void;
  timezone?: string;
}> = ({ selectedTime, onChange, onClose, timezone }) => {
  const hoursArray = Array.from({ length: 24 }, (_, i) => i);
  const minutesArray = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  const localNow = createLocalDate();
  const [hours, setHours] = useState<number>(selectedTime ? selectedTime.hour() : localNow.hour());
  const [minutes, setMinutes] = useState<number>(
    selectedTime ?
      selectedTime.minute() - (selectedTime.minute() % 5) :
      Math.floor(localNow.minute() / 5) * 5
  );

  useEffect(() => {
    // Atualiza automaticamente o tempo selecionado quando os valores de hora e minuto mudam
    const baseDate = selectedTime ? selectedTime.clone() : (timezone ? dayjs().tz(timezone) : createLocalDate());
    const newTime = baseDate.hour(hours).minute(minutes);
    onChange(newTime);
  }, [hours, minutes, onChange, timezone]);

  const handleHourClick = (hour: number) => {
    setHours(hour);
  };

  const handleMinuteClick = (minute: number) => {
    setMinutes(minute);
  };

  return (
    <div className="time-selector">
      <div className="time-selector-header">
        <div className="selected-time">
          <span>{String(hours).padStart(2, '0')}:{String(minutes).padStart(2, '0')}</span>
        </div>
      </div>

      <div className="time-selector-body">
        <div className="time-selector-column">
          <div className="time-selector-label"><span>Hora</span></div>
          <div className="time-selector-items hours custom-scrollbar">
            {hoursArray.map((hour) => (
              <div
                key={hour}
                className={`time-item ${hours === hour ? 'selected' : ''}`}
                onClick={() => handleHourClick(hour)}
              >
                <span>{String(hour).padStart(2, '0')}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="time-selector-column">
          <div className="time-selector-label"><span>Minutos</span></div>
          <div className="time-selector-items minutes custom-scrollbar">
            {minutesArray.map((minute) => (
              <div
                key={minute}
                className={`time-item ${minutes === minute ? 'selected' : ''}`}
                onClick={() => handleMinuteClick(minute)}
              >
                <span>{String(minute).padStart(2, '0')}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// WeekDaySelector component
const WeekDaySelector: React.FC<{
  selectedDate: dayjs.Dayjs | null;
  onChange: (date: dayjs.Dayjs) => void;
  onClose?: () => void;
}> = ({ selectedDate, onChange }) => {
  const weekDays = [
    { name: 'Monday', value: 1 },
    { name: 'Tuesday', value: 2 },
    { name: 'Wednesday', value: 3 },
    { name: 'Thursday', value: 4 },
    { name: 'Friday', value: 5 },
    { name: 'Saturday', value: 6 },
    { name: 'Sunday', value: 0 }
  ];

  const handleDayClick = (dayValue: number) => {
    // Determine the next occurrence of this day
    const current = selectedDate ? selectedDate : createLocalDate();
    let nextDate = current.day(dayValue);

    // If the selected day is before today (in current week) and we want next occurrence, add 1 week?
    // User logic: If I select "Monday" and today is Tuesday, do I want "Next Monday"? Yes.
    // If today is Monday and I select Monday, do I want "This Monday" (Today) or "Next Monday"? 
    // Usually Today if time hasn't passed, but let's assume we just set the day.

    if (nextDate.isBefore(createLocalDate(), 'day')) {
      nextDate = nextDate.add(1, 'week');
    }

    onChange(nextDate);
  };

  return (
    <div className="time-selector"> {/* Reusing time-selector style for consistent list view */}
      <div className="time-selector-header">
        <div className="selected-time">
          <span>Select Day</span>
        </div>
      </div>
      <div className="time-selector-body">
        <div className="time-selector-column" style={{ width: '100%' }}>
          <div className="time-selector-items custom-scrollbar" style={{ maxHeight: '200px' }}>
            {weekDays.map((day) => (
              <div
                key={day.value}
                className={`time-item ${selectedDate && selectedDate.day() === day.value ? 'selected' : ''}`}
                onClick={() => handleDayClick(day.value)}
                style={{ textAlign: 'left', paddingLeft: '1rem' }}
              >
                <span>{day.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const MessageSchedule: React.FC<MessageScheduleProps & {
  initialData?: {
    id?: number
    name?: string;
    messageText: string;
    selectedDate: dayjs.Dayjs | null;
    selectedTime: dayjs.Dayjs | null;
    selectedContactId: number | null;
    attachments: Attachment[];
    location?: string;
    timezone?: string;
    recurrenceType?: number;
    recurrenceInterval?: number;
    recurrenceEndDate?: dayjs.Dayjs | null;
    recurrenceWeekdays?: string;
    recurrenceMonthDays?: string;
    selectedRecipientIds?: number[];
    selectedGroupIds?: number[];
  }
}> = ({
  visible,
  onClose,
  onSave,
  contactId,
  addToast,
  initialData
}) => {
    const navigate = useNavigate();

    // Com o drawer aberto o botão flutuante "Registrar" sai da frente do rodapé (Cancelar/Agendar).
    useEffect(() => {
      if (!visible) return;
      document.body.classList.add('mai-overlay-open');
      return () => document.body.classList.remove('mai-overlay-open');
    }, [visible]);

    const [messageName, setMessageName] = useState<string>(initialData?.name || '');
    const [messageText, setMessageText] = useState<string>(initialData?.messageText || '');
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(initialData?.selectedDate || null);
    const [selectedTime, setSelectedTime] = useState<dayjs.Dayjs | null>(initialData?.selectedTime || null);
    const [selectedContactId, setSelectedContactId] = useState<number | null>(initialData?.selectedContactId || contactId);
    const [profileTimezone, setProfileTimezone] = useState<string>('');
    const [selectedTimezone, setSelectedTimezone] = useState<string>(initialData?.timezone || '');
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [groups, setGroups] = useState<WhatsAppGroupDto[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [contactsHasMore, setContactsHasMore] = useState(false);
    const [contactsNextOffset, setContactsNextOffset] = useState(0);
    const [isLoadingMoreContacts, setIsLoadingMoreContacts] = useState(false);
    const CONTACTS_PAGE_SIZE = 50;
    const [attachments, setAttachments] = useState<Attachment[]>(initialData?.attachments || []);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [toasts, setToasts] = useState<{ id: number; message: string; type: 'success' | 'error' | 'info' }[]>([]);

    // Recurrence State
    const [recurrenceType, setRecurrenceType] = useState<number>(initialData?.recurrenceType || 0);
    const [recurrenceInterval, setRecurrenceInterval] = useState<number>(initialData?.recurrenceInterval || 1);
    const [recurrenceEndDate, setRecurrenceEndDate] = useState<dayjs.Dayjs | null>(initialData?.recurrenceEndDate || null);
    const parseWeekdays = (s: string | undefined): number[] => {
      if (!s || !s.trim()) return [];
      return s.split(',').map(x => parseInt(x.trim(), 10)).filter(n => !isNaN(n) && n >= 0 && n <= 6);
    };
    const [recurrenceWeekdays, setRecurrenceWeekdays] = useState<number[]>(() => parseWeekdays(initialData?.recurrenceWeekdays));
    const parseMonthDays = (s: string | undefined): number[] => {
      if (!s || !s.trim()) return [];
      return s.split(',').map(x => parseInt(x.trim(), 10)).filter(n => !isNaN(n) && n >= 1 && n <= 31);
    };
    const [recurrenceMonthDays, setRecurrenceMonthDays] = useState<number[]>(() => parseMonthDays(initialData?.recurrenceMonthDays));

    // Audio State
    const [isRecording, setIsRecording] = useState(false);
    const recorderRef = useRef<RecordRTCPromisesHandler | null>(null);
    const streamRef = useRef<MediaStream | null>(null);

    // Recipients State
    const [selectedRecipientIds, setSelectedRecipientIds] = useState<number[]>(initialData?.selectedRecipientIds || (initialData?.selectedContactId ? [initialData.selectedContactId] : []));
    const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>(initialData?.selectedGroupIds || []);
    const [showRecipientsDropdown, setShowRecipientsDropdown] = useState(false);
    const [showGroupsDropdown, setShowGroupsDropdown] = useState(false);
    const [showRecurrenceDatePicker, setShowRecurrenceDatePicker] = useState(false);
    const recipientsRef = useRef<HTMLDivElement>(null);
    const groupsRef = useRef<HTMLDivElement>(null);

    // Sync recurrence weekdays when opening for edit
    useEffect(() => {
      if (visible && initialData?.recurrenceWeekdays !== undefined)
        setRecurrenceWeekdays(parseWeekdays(initialData.recurrenceWeekdays));
      if (visible && !initialData?.recurrenceWeekdays)
        setRecurrenceWeekdays([]);
    }, [visible, initialData?.id, initialData?.recurrenceWeekdays]);

    // Sync recurrence month days when opening for edit
    useEffect(() => {
      if (visible && initialData?.recurrenceMonthDays !== undefined)
        setRecurrenceMonthDays(parseMonthDays(initialData.recurrenceMonthDays));
      if (visible && !initialData?.recurrenceMonthDays)
        setRecurrenceMonthDays([]);
    }, [visible, initialData?.id, initialData?.recurrenceMonthDays]);

    // Timezone do perfil: ao abrir o drawer, buscar perfil e usar como timezone do agendamento (não há seletor de timezone)
    useEffect(() => {
      if (!visible) return;
      const token = SessionService.getToken();
      if (!token) return;
      fetch(`${config.API_BASE_URL}/users/profile`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => res.json())
        .then((json: { data?: { defaultTimezone?: string } }) => {
          const tz = json?.data?.defaultTimezone;
          if (tz && tz.trim() !== '') {
            setProfileTimezone(tz);
            if (!initialData?.timezone) setSelectedTimezone(tz);
          }
        })
        .catch(() => {});
    }, [visible, initialData?.timezone]);

    // Refs para os inputs
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const videoInputRef = useRef<HTMLInputElement>(null);
    const audioFileInputRef = useRef<HTMLInputElement>(null);
    const audioRef = useRef<HTMLAudioElement>(null);

    // Estados para dropdowns
    const [showCalendar, setShowCalendar] = useState<boolean>(false);
    const [showTimeSelector, setShowTimeSelector] = useState<boolean>(false);
    const calendarRef = useRef<HTMLDivElement>(null);
    const timeSelectorRef = useRef<HTMLDivElement>(null);

    // Novo estado para modal unificado de data e hora
    const [showDateTimeModal, setShowDateTimeModal] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<'date' | 'time'>('date');

    // Função para adicionar toasts
    const addLocalToast = (message: string, type: 'success' | 'error' | 'info') => {
      const id = Date.now();
      setToasts(prev => [...prev, { id, message, type }]);

      // Auto remove toast after 3 seconds
      setTimeout(() => {
        removeToast(id);
      }, 3000);
    };

    const removeToast = (id: number) => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    };


    // Buscar contatos e grupos
    useEffect(() => {
      const fetchData = async () => {
        try {
          // setIsLoading(true); // Don't trigger loading screen just for background fetch if visible is main trigger?
          // Logic kept same as before, assuming this is main fetch
          const token = SessionService.getToken();
          const sectorId = SessionService.getSectorId();

          if (!token || !sectorId) {
            return;
          }

          const [contactsRes, groupsRes] = await Promise.all([
            getContactsWithFilters(sectorId, false, false, CONTACTS_PAGE_SIZE, 0, 'picker').catch(err => { console.error("Error fetching contacts", err); return { data: [], pagination: null }; }),
            getWhatsAppGroups(sectorId).catch(err => { console.error("Error fetching groups", err); return { data: [] }; })
          ]);

          // Handle Contacts
          let fetchedContacts: Contact[] = [];
          if (Array.isArray(contactsRes)) {
            fetchedContacts = contactsRes;
          } else if (contactsRes && 'data' in contactsRes) {
            fetchedContacts = contactsRes.data || [];
          }
          const pag = contactsRes?.pagination;
          setContactsHasMore(pag?.hasMore ?? false);
          setContactsNextOffset(pag?.nextOffset ?? CONTACTS_PAGE_SIZE);

          // Filter out any contacts that might be groups (if your backend mixes them)
          // Adjust logic based on your actual data structure. 
          // If "isGroup" doesn't exist on Contact type yet, we might need to check other props.
          // Assuming for now we filter by basic properties or just trust the endpoint if param was available.
          // User said: "contato normal é de sincronização normal sem ser de grupo"
          // Let's filter by ensuring it doesn't look like a group if possible, or just take all "contacts"
          // as the user implies the current list HAS groups.
          // If 'isGroup' isn't on type, we can check if number ends in @g.us for example if available, 
          // or just proceed with what we have if no clear discriminator is visible in type.
          // However, user specifically asked to filter.
          // Let's look for any 'isGroup' flag or similar in the response data during runtime.
          const validContacts = fetchedContacts.filter(c => !c.number?.includes('@g.us') && !(c as any).isGroup);
          validContacts.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
          setContacts(validContacts);

          // Handle Groups
          console.log('Groups Response:', groupsRes);
          let groupsList: WhatsAppGroupDto[] = [];
          if (groupsRes && 'data' in groupsRes && Array.isArray(groupsRes.data)) {
            groupsList = groupsRes.data;
          } else {
            console.warn('Unexpected groups response structure:', groupsRes);
          }
          groupsList.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
          setGroups(groupsList);

        } catch (error) {
          console.error("Error fetching initial data", error);
        } finally {
          setIsLoading(false);
        }
      };

      if (visible) {
        fetchData();
      }
    }, [visible]);

    const loadMoreContacts = async () => {
      const sectorId = SessionService.getSectorId();
      if (!sectorId || !contactsHasMore || isLoadingMoreContacts) return;
      setIsLoadingMoreContacts(true);
      try {
        const response = await getContactsWithFilters(sectorId, false, false, CONTACTS_PAGE_SIZE, contactsNextOffset, 'picker');
        const list = (response.data || []).filter((c: Contact) => !c.number?.includes('@g.us') && !(c as any).isGroup);
        setContacts(prev => {
          const combined = [...prev, ...list];
          combined.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
          return combined;
        });
        setContactsHasMore(response.pagination?.hasMore ?? false);
        setContactsNextOffset(response.pagination?.nextOffset ?? contactsNextOffset + CONTACTS_PAGE_SIZE);
      } finally {
        setIsLoadingMoreContacts(false);
      }
    };

    // Fechar dropdowns quando clicar fora
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          calendarRef.current &&
          !calendarRef.current.contains(event.target as Node) &&
          showCalendar
        ) {
          setShowCalendar(false);
        }

        if (
          timeSelectorRef.current &&
          !timeSelectorRef.current.contains(event.target as Node) &&
          showTimeSelector
        ) {
          setShowTimeSelector(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [showCalendar, showTimeSelector]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (recipientsRef.current && !recipientsRef.current.contains(event.target as Node)) {
          setShowRecipientsDropdown(false);
        }
        if (groupsRef.current && !groupsRef.current.contains(event.target as Node)) {
          setShowGroupsDropdown(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Audio Logic
    const startRecording = async () => {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('Este navegador não permite gravar áudio');
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: { ideal: true },
            noiseSuppression: { ideal: true },
            autoGainControl: { ideal: true }
          }
        });

        streamRef.current = stream;

        recorderRef.current = new RecordRTCPromisesHandler(stream, {
          type: 'audio',
          mimeType: 'audio/webm',
          recorderType: RecordRTC.StereoAudioRecorder,
          numberOfAudioChannels: 1,
          desiredSampRate: 16000,
          bufferSize: 16384,
          timeSlice: 1000
        });

        await recorderRef.current.startRecording();
        setIsRecording(true);
      } catch (err) {
        console.error(err);
        addLocalToast('Não foi possível iniciar a gravação', 'error');
      }
    };

    const stopRecording = async () => {
      if (recorderRef.current && isRecording) {
        try {
          await recorderRef.current.stopRecording();
          const blob = await recorderRef.current.getBlob();
          const name = `Audio_${Date.now()}.webm`;
          const mimeType = 'audio/webm';
          const file = new File([blob], name, { type: mimeType });
          const preview = URL.createObjectURL(blob);

          setAttachments(prev => [
            ...prev,
            {
              fileType: 'audio',
              data: '',
              file,
              name,
              mimeType,
              preview,
              fileUrl: preview,
            }
          ]);
        } catch (error) {
          console.error('Error stopping recording:', error);
          addLocalToast('Não foi possível salvar a gravação', 'error');
        }
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      setIsRecording(false);
      recorderRef.current = null;
    };

    // Manipular seleção de arquivos
    const handleFileSelect = async (
      event: React.ChangeEvent<HTMLInputElement>,
      type: 'image' | 'video' | 'audio' | 'document'
    ) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      try {
        const file = files[0];
        const fileType: Attachment['fileType'] =
          type === 'image' ? 'image' : type === 'video' ? 'video' : type === 'audio' ? 'audio' : 'file';
        const mimeType = file.type || (type === 'video' ? 'video/mp4' : type === 'audio' ? 'audio/mpeg' : 'application/octet-stream');
        const preview = URL.createObjectURL(file);

        setAttachments(prev => [
          ...prev,
          {
            fileType,
            data: '',
            file,
            name: file.name,
            mimeType,
            preview,
            fileUrl: preview,
          }
        ]);
      } catch (error) {
        console.error('Erro ao processar arquivo:', error);
        addLocalToast('Não foi possível processar o arquivo', 'error');
      }

      event.target.value = '';
    };

    // Remover anexo
    const removeAttachment = (index: number) => {
      setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    // Renderizar preview de anexos
    const renderAttachmentsPreview = () => {
      // Sem anexos não mostra nada: os botões acima já dizem que é opcional.
      if (attachments.length === 0) return null;

      return (
        <div className="attachment-list">
          {attachments.map((attachment, index) => {
            if (attachment.fileType === 'image') {
              return (
                <div key={index} className="attachment-item">
                  <img
                    src={attachment.fileUrl || attachment.preview || (attachment.data ? `data:${attachment.mimeType};base64,${attachment.data}` : undefined)}
                    alt={attachment.name}
                  />
                  <button
                    className="remove-attachment"
                    onClick={() => removeAttachment(index)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              );
            } else if (attachment.fileType === 'audio') {
              const displayName = attachment.name || 'Audio Attachment';
              return (
                <div key={index} className="attachment-item">
                  <div className="attachment-item-content">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"></path>
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                      <line x1="12" y1="19" x2="12" y2="22"></line>
                    </svg>
                    <span>
                      {displayName.substring(0, 25)}
                      {displayName.length > 25 ? '...' : ''}
                    </span>
                  </div>
                  {attachment.fileUrl && (
                    <audio
                      ref={audioRef}
                      src={attachment.fileUrl}
                      style={{ display: 'none' }}
                    />
                  )}
                  <button
                    className="remove-attachment"
                    onClick={() => removeAttachment(index)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              );
            } else if (attachment.fileType === 'video') {
              const displayName = attachment.name || 'Video Attachment';
              const src = attachment.fileUrl || attachment.preview || (attachment.data
                ? `data:${attachment.mimeType || 'video/mp4'};base64,${attachment.data}`
                : undefined);
              return (
                <div key={index} className="attachment-item">
                  <div className="attachment-item-content">
                    <Icons.Video />
                    <span>
                      {displayName.substring(0, 25)}
                      {displayName.length > 25 ? '...' : ''}
                    </span>
                  </div>
                  {src && (
                    <video
                      src={src}
                      controls
                      style={{ maxWidth: '100%', maxHeight: 120, marginTop: 4 }}
                    />
                  )}
                  <button
                    className="remove-attachment"
                    onClick={() => removeAttachment(index)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              );
            } else {
              const displayName = attachment.name || 'File Attachment';
              return (
                <div key={index} className="attachment-item">
                  <div className="attachment-item-content">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <line x1="10" y1="9" x2="8" y2="9"></line>
                    </svg>
                    <span>
                      {displayName.substring(0, 25)}
                      {displayName.length > 25 ? '...' : ''}
                    </span>
                  </div>
                  <button
                    className="remove-attachment"
                    onClick={() => removeAttachment(index)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              );
            }
          })}
        </div>
      );
    };

    // Salvar agendamento
    const handleSave = async () => {
      if (!selectedDate || !selectedTime) {
        addLocalToast('Escolha a data e a hora do envio', 'error');
        return;
      }

      if (!messageName.trim()) {
        addLocalToast('Dê um nome ao agendamento', 'error');
        return;
      }

      // Allow saving with EITHER contacts OR groups
      if (selectedRecipientIds.length === 0 && selectedGroupIds.length === 0) {
        addLocalToast('Escolha pelo menos um contato ou grupo', 'error');
        return;
      }

      if (!messageText.trim() && attachments.length === 0) {
        addLocalToast('Escreva a mensagem ou adicione um anexo', 'error');
        return;
      }

      setIsSaving(true);

      try {
        const sectorId = SessionService.getSectorId();
        if (!sectorId) {
          addLocalToast('Nenhuma conexão selecionada', 'error');
          setIsSaving(false);
          return;
        }

        // Combinar data e hora no timezone do perfil (mesmo TZ enviado ao backend).
        // Antes usava o TZ do browser via createLocalDate, o que deslocava NextExecutionDate.
        const datePart = selectedDate.format('YYYY-MM-DD');
        const scheduledDateTime = (selectedTimezone
          ? dayjs.tz(datePart, selectedTimezone)
          : createLocalDate(selectedDate)
        )
          .hour(selectedTime.hour())
          .minute(selectedTime.minute())
          .second(0);

        // Format date as ISO string manually to strict format YYYY-MM-DDTHH:mm:ss
        // This avoids timezone shifting when using toISOString() directly if not handled carefully
        const formattedDate = scheduledDateTime.format('YYYY-MM-DDTHH:mm:ss');

        console.log('Saving schedule with date:', formattedDate);

        // Prepare Recipients
        const recipientsList: RecipientDTO[] = [];

        selectedRecipientIds.forEach(id => {
          recipientsList.push({
            recipientId: id.toString(),
            recipientType: 'Contact'
          });
        });

        selectedGroupIds.forEach(id => {
          recipientsList.push({
            recipientId: id.toString(),
            recipientType: 'Group'
          });
        });

        const resolveAttachmentPayload = async () => {
          const resolved: Array<{ type: Attachment['fileType']; data: string; name: string; mimeType: string }> = [];

          for (const att of attachments) {
            const existing = (att.data || att.fileUrl || '').trim();
            if (/^https?:\/\//i.test(existing)) {
              resolved.push({
                type: att.fileType,
                data: existing,
                name: att.name,
                mimeType: att.mimeType,
              });
              continue;
            }

            let uploadSource: File | Blob | null = att.file ?? null;
            if (!uploadSource && att.data) {
              // Legacy edit payloads still stored as base64 — upload once, then persist URL.
              const raw = att.data.includes(',') ? att.data.split(',')[1] : att.data;
              const binary = atob(raw);
              const bytes = new Uint8Array(binary.length);
              for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
              uploadSource = new File([bytes], att.name || 'attachment.bin', {
                type: att.mimeType || 'application/octet-stream',
              });
            }

            if (!uploadSource) {
              throw new Error(`Attachment "${att.name || 'file'}" has no uploadable content`);
            }

            const uploaded = await uploadMessageSchedulingAttachment(
              uploadSource,
              Number(sectorId),
              att.fileType,
              initialData?.id
            );
            resolved.push({
              type: att.fileType,
              data: uploaded.url,
              name: uploaded.name || att.name,
              mimeType: uploaded.mimeType || att.mimeType,
            });
          }

          return resolved;
        };

        const attachmentPayload = await resolveAttachmentPayload();

        const messageData: CreateMessageSchedulingDTO = {
          name: messageName,
          messageText: messageText,
          sendDate: formattedDate,
          sectorId: Number(sectorId),
          tagIds: '', // Not used in this UI?
          location: '',
          timezone: selectedTimezone,
          recurrenceType: recurrenceType,
          recurrenceInterval: 1, // Defaulting to 1 as requested
          recurrenceEndDate: recurrenceEndDate ? recurrenceEndDate.format('YYYY-MM-DD') : undefined,
          recurrenceWeekdays: recurrenceType === 2 && recurrenceWeekdays.length > 0 ? [...recurrenceWeekdays].sort((a, b) => a - b).join(',') : undefined,
          recurrenceMonthDays: recurrenceType === 3 && recurrenceMonthDays.length > 0 ? [...recurrenceMonthDays].sort((a, b) => a - b).join(',') : undefined,
          status: true, // Default active
          recipients: recipientsList,
          attachments: attachmentPayload
        };

        // For backward compatibility or if backend requires contactId, pick first one or null
        // But logic now mostly relies on 'recipients' if backend supports it.
        // If not, this might break for multiple contacts. Assuming backend updated.
        if (recipientsList.length > 0 && recipientsList[0].recipientType === 'Contact') {
          messageData.contactId = Number(recipientsList[0].recipientId);
        }

        if (initialData?.id) {
          await updateMessageScheduling(initialData.id, messageData);
          addLocalToast('Agendamento atualizado', 'success');
        } else {
          await createMessageScheduling(messageData);
          addLocalToast('Mensagem agendada', 'success');
        }

        // Reset Form
        setMessageName('');
        setMessageText('');
        setSelectedDate(null);
        setSelectedTime(null);
        setSelectedTimezone(profileTimezone);
        setAttachments([]);
        setSelectedRecipientIds([]);
        setSelectedGroupIds([]);
        setRecurrenceType(0);
        setRecurrenceInterval(1);
        setRecurrenceEndDate(null);
        setRecurrenceWeekdays([]);
        setRecurrenceMonthDays([]);

        onSave(); // Trigger refresh in parent
        onClose();
      } catch (error: any) {
        console.error('Error scheduling message:', error);
        addLocalToast(error.message || 'Não foi possível agendar a mensagem', 'error');
      } finally {
        setIsSaving(false);
      }
    };

    // Manipular mudança de data
    const handleDateChange = (date: dayjs.Dayjs) => {
      setSelectedDate(date);
    };

    // Manipular mudança de hora
    const handleTimeChange = (time: dayjs.Dayjs) => {
      setSelectedTime(time);
    };

    // Abrir modal unificado de data e hora
    const openDateTimeModal = (initialTab: 'date' | 'time' = 'date') => {
      console.log('Abrindo modal de data/hora:', initialTab);
      setShowDateTimeModal(true);
      setActiveTab(initialTab);
    };

    // Mudar para a aba de hora
    const switchToTimeTab = () => {
      setActiveTab('time');
    };

    // Fechar o modal de data/hora
    const closeDateTimeModal = () => {
      setShowDateTimeModal(false);
    };

    // Renderizar o drawer
    const renderDrawer = () => {
      if (!visible) return null;

      return (
        <div className="drawer ms-drawer">
          <div className="drawer-content ms-drawer-content">
            <div className="drawer-header ms-drawer-header">
              <div className="ms2-drawer-titles">
                <h2><span>{initialData?.id ? 'Editar agendamento' : 'Novo agendamento'}</span></h2>
                <p className="ms2-drawer-sub">O que enviar, para quem e quando.</p>
              </div>
              <button className="close-button" onClick={onClose} aria-label="Fechar">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="drawer-body ms-drawer-body">
              <section className="ms2-section">
                <header className="ms2-section-head">
                  <span className="ms2-step">1</span>
                  <div>
                    <h3>Mensagem</h3>
                    <p>O texto e os anexos que o cliente vai receber.</p>
                  </div>
                </header>
                <div className="form-group">
                  <label><span>Nome do agendamento</span><small className="ms2-label-hint">só a equipe vê</small></label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Ex.: Lembrete de pagamento de outubro"
                    value={messageName}
                    onChange={(e) => setMessageName(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label><span>Mensagem</span></label>
                  <textarea
                    className="message-textarea"
                    placeholder="Escreva a mensagem como o cliente vai receber…"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                  />
                  <span className="ms2-char-count">{messageText.length} caracteres</span>
                </div>

                <div className="form-group">
                  <label><span>Anexos</span><small className="ms2-label-hint">opcional</small></label>
                  <div className="attachment-tools ms-attachment-tools">
                    <button
                      type="button"
                      className="tool-button ms-tool-button"
                      onClick={() => imageInputRef.current?.click()}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                      <span>Imagem</span>
                    </button>

                    <button
                      type="button"
                      className="tool-button ms-tool-button"
                      onClick={() => videoInputRef.current?.click()}
                    >
                      <Icons.Video />
                      <span>Vídeo</span>
                    </button>

                    <button
                      type="button"
                      className="tool-button ms-tool-button"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <line x1="10" y1="9" x2="8" y2="9"></line>
                      </svg>
                      <span>Documento</span>
                    </button>

                    <button
                      type="button"
                      className="tool-button ms-tool-button"
                      onClick={() => audioFileInputRef.current?.click()}
                      title="Enviar arquivo de áudio"
                    >
                      <Icons.Paperclip />
                      <span>Áudio</span>
                    </button>

                    <button
                      type="button"
                      className={`tool-button ms-tool-button ${isRecording ? 'recording' : ''}`}
                      onClick={isRecording ? stopRecording : startRecording}
                    >
                      <Icons.Mic />
                      <span>{isRecording ? 'Parar gravação' : 'Gravar'}</span>
                    </button>
                  </div>

                  <input
                    type="file"
                    accept="image/*"
                    ref={imageInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect(e, 'image')}
                  />

                  <input
                    type="file"
                    accept="video/*"
                    ref={videoInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect(e, 'video')}
                  />

                  <input
                    type="file"
                    accept="audio/*"
                    ref={audioFileInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect(e, 'audio')}
                  />

                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect(e, 'document')}
                  />

                  {renderAttachmentsPreview()}
                </div>
              </section>

              <section className="ms2-section">
                <header className="ms2-section-head">
                  <span className="ms2-step">2</span>
                  <div>
                    <h3>Para quem</h3>
                    <p>Contatos e grupos do WhatsApp desta conexão.</p>
                  </div>
                </header>
                <div className="ms2-two-col">
                  <div className="form-group">
                    <label><span>Contatos</span></label>
                    <div className="recipients-selector" ref={recipientsRef}>
                      <div
                        className="recipients-input form-select"
                        onClick={() => setShowRecipientsDropdown(!showRecipientsDropdown)}
                      >
                        {selectedRecipientIds.length === 0
                          ? <span className="ms2-placeholder">Escolher contatos</span>
                          : <span>{selectedRecipientIds.length === 1 ? '1 contato selecionado' : `${selectedRecipientIds.length} contatos selecionados`}</span>}
                      </div>
                      {showRecipientsDropdown && (
                        <div className="recipients-dropdown">
                          {contacts.map(contact => (
                            <div
                              key={contact.id}
                              className={`recipient-item ${selectedRecipientIds.includes(contact.id) ? 'selected' : ''}`}
                              onClick={() => {
                                if (selectedRecipientIds.includes(contact.id)) {
                                  setSelectedRecipientIds(prev => prev.filter(id => id !== contact.id));
                                } else {
                                  setSelectedRecipientIds(prev => [...prev, contact.id]);
                                }
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedRecipientIds.includes(contact.id)}
                                readOnly
                              />
                              <span><span>{contact.name} - {getDisplayPhone(contact.number)}</span></span>
                            </div>
                          ))}
                          {contactsHasMore && (
                            <button
                              type="button"
                              className="message-schedule-load-more-contacts"
                              onClick={(e) => { e.stopPropagation(); loadMoreContacts(); }}
                              disabled={isLoadingMoreContacts}
                            >
                              {isLoadingMoreContacts ? 'Carregando…' : 'Carregar mais contatos'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label><span>Grupos</span></label>
                    <div className="recipients-selector" ref={groupsRef}>
                      <div
                        className="recipients-input form-select"
                        onClick={() => setShowGroupsDropdown(!showGroupsDropdown)}
                      >
                        {selectedGroupIds.length === 0
                          ? <span className="ms2-placeholder">Escolher grupos</span>
                          : <span>{selectedGroupIds.length === 1 ? '1 grupo selecionado' : `${selectedGroupIds.length} grupos selecionados`}</span>}
                      </div>
                      {showGroupsDropdown && (
                        <div className="recipients-dropdown">
                          {groups.length === 0 ? (
                            <div className="recipient-item ms2-dropdown-empty">
                              <span>Nenhum grupo encontrado nesta conexão</span>
                            </div>
                          ) : (
                            groups.map(group => (
                              <div
                                key={group.id}
                                className={`recipient-item ${selectedGroupIds.includes(group.id) ? 'selected' : ''}`}
                                onClick={() => {
                                  if (selectedGroupIds.includes(group.id)) {
                                    setSelectedGroupIds(prev => prev.filter(id => id !== group.id));
                                  } else {
                                    setSelectedGroupIds(prev => [...prev, group.id]);
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedGroupIds.includes(group.id)}
                                  readOnly
                                />
                                <span><span>{group.name}</span></span>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </section>

              <section className="ms2-section">
                <header className="ms2-section-head">
                  <span className="ms2-step">3</span>
                  <div>
                    <h3>Quando</h3>
                    <p>Data, hora e se o envio se repete.</p>
                  </div>
                </header>
                <div className="form-group">
                  <label><span>Data e hora do envio</span></label>
                  <div className="datetime-inputs">
                    <div className="date-picker-container">
                      <div
                        className="date-input-wrapper"
                        onClick={() => openDateTimeModal('date')}
                      >
                        <div className="date-input-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                          </svg>
                        </div>
                        <span className="date-input">
                          {selectedDate ? <span>{selectedDate.format('DD/MM/YYYY')}</span> : <span>Escolher data</span>}
                        </span>
                      </div>
                    </div>

                    <div className="time-picker-container">
                      <div
                        className="time-input-wrapper"
                        onClick={() => openDateTimeModal('time')}
                      >
                        <div className="time-input-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                          </svg>
                        </div>
                        <span className="time-input">
                          {selectedTime ? <span>{selectedTime.format('HH:mm')}</span> : <span>Escolher hora</span>}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label><span>Fuso horário</span><small className="ms2-label-hint">do seu perfil</small></label>
                  {selectedTimezone ? (
                    <TimezoneSelect
                      value={selectedTimezone}
                      onChange={() => {}}
                      disabled
                    />
                  ) : (
                    <div className="ms2-tz-warning">
                      <span>Configure o fuso horário no seu perfil para agendar no horário certo.</span>
                      <button
                        type="button"
                        onClick={() => navigate('/profile?tab=settings')}
                      >
                        Abrir perfil
                      </button>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label><span>Repetição</span></label>
                  <div className="recurrence-options">
                    <select
                      className="form-select"
                      value={recurrenceType}
                      onChange={(e) => setRecurrenceType(Number(e.target.value))}
                    >
                      <option value={0}>Não repetir</option>
                      <option value={1}>Todos os dias</option>
                      <option value={2}>Toda semana</option>
                      <option value={3}>Todo mês</option>
                    </select>

                    {recurrenceType > 0 && (
                      recurrenceType === 2 ? (
                        <div className="form-group" style={{ marginTop: '1rem' }}>
                          <label><span>Repetir em</span></label>
                          <div className="recurrence-weekdays ms2-toggle-row">
                            {[
                              { value: 0, label: 'Dom' },
                              { value: 1, label: 'Seg' },
                              { value: 2, label: 'Ter' },
                              { value: 3, label: 'Qua' },
                              { value: 4, label: 'Qui' },
                              { value: 5, label: 'Sex' },
                              { value: 6, label: 'Sáb' }
                            ].map(({ value, label }) => (
                              <label key={value} className={`ms2-toggle${recurrenceWeekdays.includes(value) ? ' is-on' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={recurrenceWeekdays.includes(value)}
                                  onChange={() => {
                                    setRecurrenceWeekdays(prev =>
                                      prev.includes(value) ? prev.filter(d => d !== value) : [...prev, value].sort((a, b) => a - b)
                                    );
                                  }}
                                />
                                <span>{label}</span>
                              </label>
                            ))}
                          </div>
                          {recurrenceWeekdays.length > 0 && (
                            <p className="form-hint ms2-hint">
                              Envia toda {[...recurrenceWeekdays].sort((a,b)=>a-b).map(d => ['dom','seg','ter','qua','qui','sex','sáb'][d]).join(', ')}
                            </p>
                          )}
                        </div>
                      ) : recurrenceType === 3 ? (
                        <>
                          <div className="form-group" style={{ marginTop: '1rem' }}>
                            <label><span>Repetir nos dias do mês</span></label>
                            <div className="recurrence-month-days ms2-toggle-grid">
                              {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                <label key={day} className={`ms2-toggle is-day${recurrenceMonthDays.includes(day) ? ' is-on' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={recurrenceMonthDays.includes(day)}
                                    onChange={() => {
                                      setRecurrenceMonthDays(prev =>
                                        prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort((a, b) => a - b)
                                      );
                                    }}
                                  />
                                  <span>{day}</span>
                                </label>
                              ))}
                            </div>
                            {recurrenceMonthDays.length > 0 && (
                              <p className="form-hint ms2-hint">
                                Envia nos dias {[...recurrenceMonthDays].sort((a,b)=>a-b).join(', ')} de cada mês
                              </p>
                            )}
                          </div>
                          <div className="recurrence-details">
                            <div className="form-group" style={{ marginTop: '1rem' }}>
                              <label><span>Termina em</span><small className="ms2-label-hint">opcional</small></label>
                              <div
                                className="date-input-wrapper"
                                onClick={() => setShowRecurrenceDatePicker(true)}
                              >
                                <span className="date-input">
                                  {recurrenceEndDate ?
                                    recurrenceEndDate.format('DD/MM/YYYY')
                                    : <span className="ms2-placeholder">Sem data final</span>
                                  }
                                </span>
                              </div>
                            </div>
                            {showRecurrenceDatePicker && (
                              <div className="datetime-overlay" onClick={() => setShowRecurrenceDatePicker(false)}>
                                <div className="unified-datetime-modal" onClick={e => e.stopPropagation()} style={{ height: 'auto', minHeight: 'auto' }}>
                                  <div className="unified-datetime-header">
                                    <h3 className="unified-datetime-title"><span>Data final</span></h3>
                                    <button className="unified-datetime-close" onClick={() => setShowRecurrenceDatePicker(false)}>×</button>
                                  </div>
                                  <div className="unified-datetime-content">
                                    <CustomCalendar
                                      selectedDate={recurrenceEndDate}
                                      onChange={(date) => {
                                        setRecurrenceEndDate(date);
                                        setShowRecurrenceDatePicker(false);
                                      }}
                                      viewMode="month"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        /* Standard End Date Picker for other recurrence types (like Monthly) if needed, OR user implies End Date is irrelevant */
                        /* User said: "mensal ta trazendo datepicker de mes" -> implied for Monthly they MIGHT want Month picker for End Date, 
                           but previously said "should be only week without day or year" for WEEKLY.
                           So for non-weekly, we keep the End Date Logic? Or hide it?
                           Let's keep End Date for others for now. 
                        */
                        <div className="recurrence-details">
                          <div className="form-group" style={{ marginTop: '1rem' }}>
                            <label><span>Termina em</span><small className="ms2-label-hint">opcional</small></label>
                            <div
                              className="date-input-wrapper"
                              onClick={() => setShowRecurrenceDatePicker(true)}
                            >
                              <span className="date-input">
                                {recurrenceEndDate ?
                                  recurrenceEndDate.format('DD/MM/YYYY')
                                  : <span className="ms2-placeholder">Sem data final</span>
                                }
                              </span>
                            </div>
                          </div>

                          {showRecurrenceDatePicker && (
                            <div className="datetime-overlay" onClick={() => setShowRecurrenceDatePicker(false)}>
                              <div className="unified-datetime-modal" onClick={e => e.stopPropagation()} style={{ height: 'auto', minHeight: 'auto' }}>
                                <div className="unified-datetime-header">
                                  <h3 className="unified-datetime-title"><span>Data final</span></h3>
                                  <button className="unified-datetime-close" onClick={() => setShowRecurrenceDatePicker(false)}>×</button>
                                </div>
                                <div className="unified-datetime-content">
                                  <CustomCalendar
                                    selectedDate={recurrenceEndDate}
                                    onChange={(date) => {
                                      setRecurrenceEndDate(date);
                                      setShowRecurrenceDatePicker(false);
                                    }}
                                    viewMode={recurrenceType === 3 ? 'month' : 'day'}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              </section>
            </div>

            <div className="drawer-footer">
              <button
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSaving}
              >
                <span>Cancelar</span>
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={isSaving}
              >
                <span>{isSaving ? 'Salvando…' : initialData?.id ? 'Salvar alterações' : 'Agendar envio'}</span>
              </button>
            </div>
          </div>

          {/* Modal unificado para data e hora */}
          {showDateTimeModal && (
            <div className="datetime-overlay" onClick={closeDateTimeModal}>
              <div className="unified-datetime-modal" onClick={(e) => e.stopPropagation()}>
                <div className="unified-datetime-header">
                  <h3 className="unified-datetime-title">
                    <span>{activeTab === 'date' ? 'Escolher data' : 'Escolher hora'}</span>
                  </h3>
                  <button
                    className="unified-datetime-close"
                    onClick={closeDateTimeModal}
                  >
                    ×
                  </button>
                </div>

                <div className="unified-datetime-tabs">
                  <button
                    className={`unified-datetime-tab ${activeTab === 'date' ? 'active' : ''}`}
                    onClick={() => setActiveTab('date')}
                  >
                    <span>Data</span>
                  </button>
                  <button
                    className={`unified-datetime-tab ${activeTab === 'time' ? 'active' : ''}`}
                    onClick={() => setActiveTab('time')}
                  >
                    <span>Hora</span>
                  </button>
                </div>

                <div className="unified-datetime-content">
                  {activeTab === 'date' && (
                    <CustomCalendar
                      selectedDate={selectedDate}
                      timezone={selectedTimezone}
                      onChange={(date) => {
                        handleDateChange(date);
                        // Troca automaticamente para a aba de hora após selecionar a data
                        setActiveTab('time');
                      }}
                    />
                  )}

                  {activeTab === 'time' && (
                    <TimeSelector
                      selectedTime={selectedTime || (selectedDate ? createLocalDate(selectedDate) : createLocalDate())}
                      timezone={selectedTimezone}
                      onChange={handleTimeChange}
                    />
                  )}
                </div>

                <div className="unified-datetime-actions">
                  <button
                    className="unified-datetime-cancel"
                    onClick={closeDateTimeModal}
                  >
                    <span>Cancelar</span>
                  </button>
                  <button
                    className="unified-datetime-confirm"
                    onClick={closeDateTimeModal}
                  >
                    <span>Confirmar</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          <ToastContainer>
            {toasts.map(toast => (
              <Toast
                key={toast.id}
                message={toast.message}
                type={toast.type}
                onClose={() => removeToast(toast.id)}
              />
            ))}
          </ToastContainer>
        </div>
      );
    };

    return renderDrawer();
  };

const MessageScheduleScreen: React.FC = () => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState<Blob | null>(null);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [isEmojiPickerVisible, setIsEmojiPickerVisible] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState<number | null>(null);
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(null);
  const [isDeletingSchedule, setIsDeletingSchedule] = useState(false);
  const [flows, setFlows] = useState([]);
  const [selectedSector, setSelectedSector] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewURL, setPreviewURL] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState<Message>({
    title: '',
    date: null,
    description: '',
    labels: [],
    contactId: null,
    contact: {} as Contact
  });
  const [dateInputValue, setDateInputValue] = useState('');
  const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string | null }>({});
  const [toasts, setToasts] = useState<{ id: number; message: string; type: 'success' | 'error' | 'info' }[]>([]);
  const [isSavingAudio, setIsSavingAudio] = useState(false);
  const [drawerInitialData, setDrawerInitialData] = useState<any>(undefined);
  const [schedulingsHasMore, setSchedulingsHasMore] = useState(false);
  const [schedulingsPage, setSchedulingsPage] = useState(1);
  const [schedulingsTotalPages, setSchedulingsTotalPages] = useState(1);
  const [schedulingsTotalCount, setSchedulingsTotalCount] = useState(0);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [showConfigureTimezoneModal, setShowConfigureTimezoneModal] = useState(false);
  const SCHEDULINGS_PAGE_SIZE = 50;
  const navigate = useNavigate();

  const addToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = Date.now();
    setToasts(current => [...current, { id, message, type }]);
    setTimeout(() => removeToast(id), 3000);
  };

  const removeToast = (id: number) => {
    setToasts(current => current.filter(toast => toast.id !== id));
  };

  const renderDrawer = () => {
    if (!isDrawerVisible) return null;
    return (
      <MessageSchedule
        visible={isDrawerVisible}
        onClose={() => setIsDrawerVisible(false)}
        onSave={() => {
          setIsDrawerVisible(false);
          fetchInitialData();
        }}
        contactId={newMessage.contactId || 0}
        addToast={addToast}
        initialData={drawerInitialData}
      />
    );
  };


  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const renderAttachmentsPreview = () => {
    if (!attachments.length) {
      return null;
    }

    const images = attachments.filter(att => att.fileType === 'image');
    const audios = attachments.filter(att => att.fileType === 'audio');
    const videos = attachments.filter(att => att.fileType === 'video');
    const documents = attachments.filter(att => att.fileType === 'file');

    return (
      <div className="attachments-preview">
        {images.length > 0 && (
          <div className="attachment-section">
            <h4><span>Images ({images.length})</span></h4>
            <div className="attachment-list">
              {images.map((att, index) => (
                <div key={index} className="attachment-item">
                  <div className="attachment-item-content">
                    <Icons.Picture />
                    <span className="attachment-name">
                      {att.name.substring(0, 25)}
                      {att.name.length > 25 ? '...' : ''}
                    </span>
                  </div>
                  <button onClick={() => removeAttachment(attachments.indexOf(att))} className="remove-attachment">
                    <Icons.Delete />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {videos.length > 0 && (
          <div className="attachment-section">
            <h4><span>Videos ({videos.length})</span></h4>
            <div className="attachment-list">
              {videos.map((att, index) => (
                <div key={index} className="attachment-item">
                  <div className="attachment-item-content">
                    <Icons.Video />
                    <span className="attachment-name">
                      {att.name.substring(0, 25)}
                      {att.name.length > 25 ? '...' : ''}
                    </span>
                  </div>
                  <button onClick={() => removeAttachment(attachments.indexOf(att))} className="remove-attachment">
                    <Icons.Delete />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {audios.length > 0 && (
          <div className="attachment-section">
            <h4><span>Audios ({audios.length})</span></h4>
            <div className="attachment-list">
              {audios.map((att, index) => (
                <div key={index} className="attachment-item">
                  <div className="attachment-item-content">
                    <Icons.Mic />
                    <span className="attachment-name">
                      {att.name.substring(0, 25)}
                      {att.name.length > 25 ? '...' : ''}
                    </span>
                  </div>
                  <button onClick={() => removeAttachment(attachments.indexOf(att))} className="remove-attachment">
                    <Icons.Delete />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {documents.length > 0 && (
          <div className="attachment-section">
            <h4><span>Documents ({documents.length})</span></h4>
            <div className="attachment-list">
              {documents.map((att, index) => (
                <div key={index} className="attachment-item">
                  <div className="attachment-item-content">
                    <Icons.Paperclip />
                    <span className="attachment-name">
                      {att.name.substring(0, 25)}
                      {att.name.length > 25 ? '...' : ''}
                    </span>
                  </div>
                  <button onClick={() => removeAttachment(attachments.indexOf(att))} className="remove-attachment">
                    <Icons.Delete />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const fetchInitialData = async () => {
    setIsLoading(true);
    try {
      const sectorId = SessionService.getSectorId();
      setSelectedSector(sectorId);

      if (!sectorId) {
        console.log('Nenhum setor selecionado, limpando dados');
        setMessages([]);
        setTags([]);
        setContacts([]);
        setSchedulingsHasMore(false);
        setSchedulingsPage(1);
        setSchedulingsTotalPages(1);
        setSchedulingsTotalCount(0);
        return;
      }

      console.log('Buscando dados para o setor:', sectorId);

      // Buscar tags, contatos (primeira página) e agendamentos (primeira página)
      const [tagsResponse, contactsResponse, pagedSchedulings] = await Promise.all([
        getTags(sectorId).catch(error => {
          console.error('Erro ao buscar tags:', error);
          return { data: [] };
        }),
        getContactsWithFilters(sectorId, false, false, 50, 0, 'picker').catch(error => {
          return { data: [], pagination: null };
        }),
        getMessageSchedulingsPaged(sectorId, 1, SCHEDULINGS_PAGE_SIZE).catch(error => {
          console.error('Erro ao buscar agendamentos paginados:', error);
          return { items: [], totalCount: 0, pageNumber: 1, pageSize: SCHEDULINGS_PAGE_SIZE, totalPages: 0, hasNextPage: false, hasPreviousPage: false };
        })
      ]);

      if (Array.isArray(tagsResponse.data)) {
        setTags(tagsResponse.data);
      } else {
        setTags([]);
      }

      const contactsList = Array.isArray(contactsResponse.data) ? contactsResponse.data : [];
      contactsList.sort((a: Contact, b: Contact) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
      setContacts(contactsList);

      const mapScheduleSummaryToMessage = (dto: { id: number; title?: string; contactCount: number; scheduledTime: string; status: string; messagePreview: string; timezone?: string | null }): Message => {
        const dateVal = utcInstantMs(dto.scheduledTime);
        return {
          id: dto.id,
          title: (dto.title && dto.title.trim()) ? dto.title : (dto.messagePreview?.slice(0, 50) || 'Schedule'),
          date: dateVal,
          description: dto.messagePreview || '',
          labels: [],
          contactId: null,
          contact: { name: `${dto.contactCount} recipient(s)`, id: 0, number: '', sectorId: 0, isActive: true } as Contact,
          attachments: [],
          status: dto.status,
          timezone: dto.timezone || undefined,
        };
      };

      const formatted = (pagedSchedulings.items || []).map(mapScheduleSummaryToMessage);
      setMessages(formatted);
      setSchedulingsHasMore(pagedSchedulings.hasNextPage ?? false);
      setSchedulingsPage(1);
      setSchedulingsTotalPages(pagedSchedulings.totalPages ?? 1);
      setSchedulingsTotalCount(pagedSchedulings.totalCount ?? 0);
    } catch (error) {
      console.error('Error loading data:', error);
      addToast('Não foi possível carregar os agendamentos', 'error');
      setMessages([]);
      setTags([]);
      setContacts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();

    const handleSectorChange = () => {
      fetchInitialData();
    };

    window.addEventListener('sectorChanged', handleSectorChange);

    return () => {
      window.removeEventListener('sectorChanged', handleSectorChange);
    };
  }, []);

  const mapDtoToMessage = (dto: { id: number; title?: string; contactCount: number; scheduledTime: string; status: string; messagePreview: string; timezone?: string | null }): Message => ({
    id: dto.id,
    title: (dto.title && dto.title.trim()) ? dto.title : (dto.messagePreview?.slice(0, 50) || 'Schedule'),
    date: utcInstantMs(dto.scheduledTime),
    description: dto.messagePreview || '',
    labels: [],
    contactId: null,
    contact: { name: `${dto.contactCount} recipient(s)`, id: 0, number: '', sectorId: 0, isActive: true } as Contact,
    attachments: [],
    status: dto.status,
    timezone: dto.timezone || undefined,
  });

  const goToPage = async (page: number) => {
    const sectorId = SessionService.getSectorId();
    if (!sectorId || page < 1 || page > schedulingsTotalPages || isLoadingPage) return;
    setIsLoadingPage(true);
    try {
      const paged = await getMessageSchedulingsPaged(sectorId, page, SCHEDULINGS_PAGE_SIZE);
      const formatted = (paged.items || []).map(mapDtoToMessage);
      setMessages(formatted);
      setSchedulingsHasMore(paged.hasNextPage ?? false);
      setSchedulingsPage(page);
      setSchedulingsTotalPages(paged.totalPages ?? 1);
      setSchedulingsTotalCount(paged.totalCount ?? 0);
    } catch (err) {
      console.error('Error loading page:', err);
      addToast('Não foi possível carregar a página', 'error');
    } finally {
      setIsLoadingPage(false);
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string | null } = {};

    if (!newMessage.title.trim()) {
      newErrors.title = null;
    }

    if (!newMessage.date) {
      newErrors.date = null;
    }

    if (!newMessage.description.trim()) {
      newErrors.description = null;
    }

    if (!newMessage.contactId) {
      newErrors.contactId = null;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDelete = async (index: number) => {
    const messageId = messages[index].id;
    if (!messageId) return;

    setIsDeletingSchedule(true);
    setIsLoading(true);
    try {
      await deleteMessageScheduling(messageId);
      addToast('Agendamento excluído', 'success');
      await fetchInitialData();
    } catch (error: any) {
      console.error('Erro ao excluir mensagem:', error);
      addToast(error.response?.data?.message || 'Não foi possível excluir o agendamento', 'error');
    } finally {
      setConfirmDeleteIndex(null);
      setIsDeletingSchedule(false);
      setIsLoading(false);
    }
  };

  const showDrawer = async (index: number | null = null, clone: boolean = false) => {
    if (index !== null) {
      const message = messages[index];
      setCurrentMessageIndex(clone ? null : index);
      setIsLoading(true);
      try {
        const data = await getMessageScheduling(message.id!);
        console.log("Dados recebidos do agendamento:", data);

        // Clonagem: abre o drawer como "novo" (sem id → cria outro), mas herdando TODOS os campos
        // do agendamento clonado. O usuário só ajusta o que precisar e salva.
        const idForDrawer = clone ? undefined : data.id;
        const nameForDrawer = clone ? `${(data.name || '').trim()} (cópia)`.trim() : data.name;

        // Parse da data ISO para dayjs
        // Parse da data ISO para dayjs
        const dateObj = data.timezone ? dayjs.tz(data.scheduledDate || data.sendDate, data.timezone) : dayjs(data.scheduledDate || data.sendDate);

        // Parse recipients to set selection
        const contactIds: number[] = [];
        const groupIds: number[] = [];

        if (data.recipients && Array.isArray(data.recipients)) {
          data.recipients.forEach((r: any) => {
            if (r.recipientType === 'Contact') {
              contactIds.push(Number(r.recipientId));
            } else if (r.recipientType === 'Group') {
              groupIds.push(Number(r.recipientId));
            }
          });
        } else if (data.contactId) {
          // Fallback
          contactIds.push(data.contactId);
        }

        setNewMessage({
          id: idForDrawer,
          title: nameForDrawer,
          date: dateObj.isValid() ? dateObj.valueOf() : null,
          description: data.messageText || data.content || '',
          labels: data.tagIds ? data.tagIds.split(',') : [],
          contactId: data.contactId ?? data.contact_id ?? null,
          contact: contacts.find(c => c.id === (data.contactId ?? data.contact_id)) || {} as Contact,
        });

        // Set Dropdowns
        // Set Location


        // Mapear anexos para o formato esperado pelo componente
        const mappedAttachments = (data.attachments || []).map((att: any): Attachment => {
          const rawContent = att.content || att.data || '';
          const attType = (att.type || att.fileType || '').toLowerCase();
          const mime = (att.mimeType || '').toLowerCase();
          const fileType: Attachment['fileType'] =
            attType === 'image' || mime.startsWith('image/') ? 'image'
              : attType === 'audio' || mime.startsWith('audio/') ? 'audio'
                : attType === 'video' || mime.startsWith('video/') ? 'video'
                  : 'file';
          const isUrl = /^https?:\/\//i.test((rawContent || '').trim()) || /^https?:\/\//i.test((att.fileUrl || '').trim());
          const url = isUrl ? (att.fileUrl || rawContent).trim() : undefined;
          return {
            fileType,
            data: url || rawContent,
            name: att.name || att.fileName || 'file',
            preview: url || (rawContent ? `data:${att.mimeType || 'application/octet-stream'};base64,${rawContent}` : undefined),
            fileUrl: url || att.fileUrl,
            mimeType: att.mimeType || 'application/octet-stream'
          };
        });

        setAttachments(mappedAttachments);
        setIsDrawerVisible(true);


        setDrawerInitialData({
          id: idForDrawer,
          name: nameForDrawer,
          messageText: data.messageText || data.content || '',
          selectedDate: dateObj,
          selectedTime: dateObj,
          selectedContactId: data.contactId ?? data.contact_id,
          attachments: mappedAttachments,
          location: data.location || '',
          timezone: data.timezone || '',
          recurrenceType: data.recurrenceType || 0,
          recurrenceInterval: data.recurrenceInterval || 1,
          recurrenceEndDate: data.recurrenceEndDate ? dayjs(data.recurrenceEndDate) : null,
          recurrenceWeekdays: data.recurrenceWeekdays,
          recurrenceMonthDays: data.recurrenceMonthDays,
          selectedRecipientIds: contactIds,
          selectedGroupIds: groupIds
        });
      } catch (error) {
        console.error("Erro ao buscar dados do agendamento:", error);
        addToast('Não foi possível abrir o agendamento', 'error');

        setNewMessage({
          id: clone ? undefined : message.id,
          title: clone ? `${(message.title || '').trim()} (cópia)`.trim() : message.title,
          date: message.date,
          description: message.description,
          labels: message.labels,
          contactId: message.contactId,
          contact: message.contact,
        });
        setAttachments([]);
        setDrawerInitialData(undefined);
        setIsDrawerVisible(true);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Novo agendamento: exige timezone configurado no perfil
      const token = SessionService.getToken();
      if (!token) {
        setShowConfigureTimezoneModal(true);
        return;
      }
      try {
        const res = await fetch(`${config.API_BASE_URL}/users/profile`, { headers: { Authorization: `Bearer ${token}` } });
        const json = await res.json();
        const tz = json?.data?.defaultTimezone;
        if (!tz || typeof tz !== 'string' || !tz.trim()) {
          setShowConfigureTimezoneModal(true);
          return;
        }
      } catch {
        setShowConfigureTimezoneModal(true);
        return;
      }
      setNewMessage({
        title: '',
        date: null,
        description: '',
        labels: [],
        contactId: null,
        contact: {} as Contact
      });
      setAttachments([]);

      setDrawerInitialData(undefined);
      setIsDrawerVisible(true);
    }
  };

  const closeDrawer = () => {
    setIsDrawerVisible(false);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDateInputValue(value);

    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        setNewMessage({ ...newMessage, date: date.getTime() });
        if (errors.date !== undefined) {
          setErrors({ ...errors, date: null });
        }
      }
    } else {
      setNewMessage({ ...newMessage, date: null });
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage({
      ...newMessage,
      description: e.target.value,
    });
  };

  const removeTag = () => {
    setNewMessage(prev => ({
      ...prev,
      labels: []
    }));
  };

  const toggleTagSelection = (tagId: string) => {
    setNewMessage(prev => ({
      ...prev,
      labels: [tagId]
    }));
    setIsTagsDropdownOpen(false);
  };

  /** Data no fuso do agendamento (ou do navegador), como Date "de parede" para dateParts. */
  const wallDate = (message: Message): Date | null => {
    if (message.date == null) return null;
    const w = message.timezone ? dayjs(message.date).tz(message.timezone) : dayjs(message.date);
    return w.isValid() ? new Date(w.year(), w.month(), w.date(), w.hour(), w.minute()) : null;
  };

  const renderMessageCard = (message: Message, index: number) => {
    const nowMs = Date.now();
    const wall = wallDate(message);
    const parts = wall ? dateParts(wall) : null;
    const status = scheduleStatus(message.status, message.date, nowMs);
    const attachmentsCount = message.attachments?.length ?? 0;
    return (
      <article className={`ms2-card is-${status.tone}`}>
        <div className="ms2-card-top">
          <div className="ms2-date-tile">
            <span className="ms2-date-month">{parts?.month ?? '—'}</span>
            <span className="ms2-date-day">{parts?.day ?? '--'}</span>
          </div>
          <div className="ms2-card-head">
            <h3 className="ms2-card-title" title={message.title}>{message.title}</h3>
            <p className="ms2-card-when">
              {parts ? `${parts.weekday}, ${parts.time}` : 'Sem data'}
              {message.date != null && <span className="ms2-card-rel"> · {relativeWhen(message.date, nowMs)}</span>}
            </p>
          </div>
          <div className="ms2-card-actions">
            <button
              type="button"
              className="ms2-icon-btn"
              title="Duplicar (cria um novo já preenchido)"
              aria-label="Duplicar agendamento"
              onClick={() => showDrawer(index, true)}
            >
              <Icons.Clone />
              <span className="ms2-icon-label">Duplicar</span>
            </button>
            <button
              type="button"
              className="ms2-icon-btn"
              title="Editar"
              aria-label="Editar agendamento"
              onClick={() => showDrawer(index)}
            >
              <Icons.Edit />
              <span className="ms2-icon-label">Editar</span>
            </button>
            <button
              type="button"
              className="ms2-icon-btn is-danger"
              title="Excluir"
              aria-label="Excluir agendamento"
              onClick={() => setConfirmDeleteIndex(index)}
            >
              <Icons.Delete />
              <span className="ms2-icon-label">Excluir</span>
            </button>
          </div>
        </div>

        <p className="ms2-card-message">{message.description || 'Sem texto, só anexos.'}</p>

        <div className="ms2-card-foot">
          <span className="ms2-chip">{recipientsLabel(message.contact?.name)}</span>
          {attachmentsCount > 0 && (
            <span className="ms2-chip">
              <Icons.Paperclip /> {attachmentsCount}
            </span>
          )}
          <span className={`ms2-status is-${status.tone}`}>{status.label}</span>
        </div>
      </article>
    );
  };

  const summaryNow = Date.now();
  const nextSend = messages
    .filter((m) => m.date != null && scheduleStatus(m.status, m.date, summaryNow).tone === 'scheduled')
    .sort((a, b) => (a.date as number) - (b.date as number))[0];
  const nextSendWall = nextSend ? wallDate(nextSend) : null;
  const nextSendParts = nextSendWall ? dateParts(nextSendWall) : null;
  const failedCount = messages.filter((m) => scheduleStatus(m.status, m.date, summaryNow).tone === 'failed').length;

  return (
    <div className="message-schedule-wrapper ms2-page">
      <div className="message-schedule-container">
        {isLoading && (
          <div className="loading-overlay">
            <LoadingSpinner size="lg" color="primary" />
          </div>
        )}

        <div className="message-schedule-header ms2-header">
          <div className="message-schedule-header-content">
            <span className="ms2-eyebrow">Mensagens programadas</span>
            <h1><span>Agendamentos</span></h1>
            <p className="message-schedule-header-description">
              <span>Lembretes, follow-ups e campanhas que saem sozinhos, na hora certa, para contatos e grupos.</span>
            </p>
          </div>
          <button
            className="message-schedule-add-button ms2-add"
            onClick={() => showDrawer(null)}
            disabled={!selectedSector}
          >
            <Icons.Plus />
            <span>Novo agendamento</span>
          </button>
        </div>

        {selectedSector && schedulingsTotalCount > 0 && (
          <div className="ms2-summary" aria-label="Resumo dos agendamentos">
            <div className="ms2-stat">
              <span className="ms2-stat-label">Total</span>
              <span className="ms2-stat-value">{schedulingsTotalCount}</span>
              <span className="ms2-stat-hint">{schedulingsTotalCount === 1 ? 'agendamento' : 'agendamentos'}</span>
            </div>
            <div className="ms2-stat is-next">
              <span className="ms2-stat-label">Próximo envio</span>
              <span className="ms2-stat-value">
                {nextSendParts && nextSend ? `${nextSendParts.weekday} ${nextSendParts.day} ${nextSendParts.month}, ${nextSendParts.time}` : '—'}
              </span>
              <span className="ms2-stat-hint" title={nextSend?.title}>
                {nextSend ? `${nextSend.title} · ${relativeWhen(nextSend.date, summaryNow)}` : 'Nenhum envio futuro nesta página'}
              </span>
            </div>
            <div className={`ms2-stat${failedCount > 0 ? ' is-alert' : ''}`}>
              <span className="ms2-stat-label">Falhas</span>
              <span className="ms2-stat-value">{failedCount}</span>
              <span className="ms2-stat-hint">{failedCount > 0 ? 'abra e reenvie' : 'tudo certo nesta página'}</span>
            </div>
          </div>
        )}

        <div className="message-schedule-content">
          {!selectedSector ? (
            <div className="no-sector-text">
              <h3><span>Nenhuma conexão selecionada</span></h3>
              <p><span>Escolha uma conexão no menu para ver os agendamentos.</span></p>
            </div>
          ) : (
            <>
              <div className="message-schedule-grid-wrapper">
                <div className="message-schedule-grid">
                  {!isLoading && messages.length === 0 ? (
                    <div className="message-schedule-empty message-schedule-empty-in-grid ms2-empty">
                      <div className="message-schedule-empty-icon">
                        <Icons.Calendar />
                      </div>
                      <h3>Nenhum agendamento ainda</h3>
                      <p>Programe lembretes, follow-ups ou campanhas para saírem na hora certa.</p>
                      <button type="button" className="ms2-empty-cta" onClick={() => showDrawer(null)}>
                        <Icons.Plus /> Criar o primeiro
                      </button>
                    </div>
                  ) : (
                    messages.map((message, index) => (
                      <React.Fragment key={message.id || index}>
                        {renderMessageCard(message, index)}
                      </React.Fragment>
                    ))
                  )}
                </div>
              </div>
              {schedulingsTotalPages >= 1 && schedulingsTotalCount > 0 && (
                <div className="message-schedule-pagination-wrap">
                  <button
                    type="button"
                    className="message-schedule-pagination-btn"
                    onClick={() => goToPage(schedulingsPage - 1)}
                    disabled={schedulingsPage <= 1 || isLoadingPage}
                  >
                    Anterior
                  </button>
                  {Array.from({ length: Math.min(5, Math.max(1, schedulingsTotalPages)) }, (_, i) => {
                    let pageNum: number;
                    if (schedulingsTotalPages <= 5) {
                      pageNum = i + 1;
                    } else if (schedulingsPage <= 3) {
                      pageNum = i + 1;
                    } else if (schedulingsPage >= schedulingsTotalPages - 2) {
                      pageNum = schedulingsTotalPages - 4 + i;
                    } else {
                      pageNum = schedulingsPage - 2 + i;
                    }
                    if (pageNum < 1 || pageNum > schedulingsTotalPages) return null;
                    return (
                      <button
                        key={pageNum}
                        type="button"
                        className={`message-schedule-pagination-btn ${schedulingsPage === pageNum ? 'active' : ''}`}
                        onClick={() => goToPage(pageNum)}
                        disabled={isLoadingPage}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    className="message-schedule-pagination-btn"
                    onClick={() => goToPage(schedulingsPage + 1)}
                    disabled={schedulingsPage >= schedulingsTotalPages || isLoadingPage}
                  >
                    Próxima
                  </button>
                  <span className="ms2-page-info">
                    Página {schedulingsPage} de {schedulingsTotalPages} · {schedulingsTotalCount} no total
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {renderDrawer()}

      <ConfirmModal
        open={confirmDeleteIndex !== null}
        onClose={() => setConfirmDeleteIndex(null)}
        onConfirm={() => { if (confirmDeleteIndex !== null) handleDelete(confirmDeleteIndex); }}
        title="Excluir agendamento"
        message="Tem certeza? O agendamento será removido e não poderá ser recuperado."
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
        loading={isDeletingSchedule}
      />

      <ConfigureTimezoneModal
        visible={showConfigureTimezoneModal}
        onCancel={() => setShowConfigureTimezoneModal(false)}
      />

      <ToastContainer>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </ToastContainer>
    </div>
  );
};

export default MessageScheduleScreen;
